import Autocomplete from '@components/v2/form/Autocomplete/Autocomplete';
import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { FilterOptionsState, MenuItem, Stack } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { Plus } from 'lucide-react';
import * as R from 'ramda';
import { useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useListPick } from '../../../components/ListPicker/useListPick';
import LangEnum from '../../../lang/LangEnum';
import { onScrollEndReload } from '../../../utils/generics';
import { useTranslation } from '../../translation/TranslationContext';
import { PosFormValues } from '../pos.schema';
import { AutocompletePointOfSaleType } from '../pos.types';

const OPTIONS_MAX_HEIGHT = 248;

const filterPointsOfSale = createFilterOptions<AutocompletePointOfSaleType>();

interface PosAutocompleteProps {
  control: Control<PosFormValues>;
  name: 'pointOfSale';
}

const PosAutocomplete = ({ control, name }: PosAutocompleteProps) => {
  const { translate } = useTranslation();
  const [inputText, setInputText] = useState('');

  const listPickActions = useListPick<AutocompletePointOfSaleType>({
    url: 'store-points-of-sale/points-of-sale',
    queryField: 'name',
    limit: 20,
  });

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const options: AutocompletePointOfSaleType[] = R.uniqBy(
          (pointOfSale) => pointOfSale.name,
          field.value ? [field.value, ...listPickActions.leftItems] : listPickActions.leftItems,
        );

        return (
          <Autocomplete<AutocompletePointOfSaleType>
            value={field.value ?? null}
            inputValue={inputText}
            options={options}
            loading={listPickActions.loading}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, selected) => option.name === selected.name}
            textFieldProps={{
              label: translate(LangEnum.POS_SELECT_LABEL),
              error: !!fieldState.error,
              helperText: fieldState.error?.message,
              name: field.name,
            }}
            slotProps={{
              listbox: {
                style: { maxHeight: OPTIONS_MAX_HEIGHT },
                onScroll: (event) => onScrollEndReload(event, listPickActions),
              },
            }}
            onInputChange={(_, value, reason) => {
              if (reason === 'blur') {
                return;
              }
              setInputText(value);
              listPickActions.onSearch(value);
            }}
            onChange={(_, newValue) => {
              if (!newValue) {
                field.onChange(null);
                return;
              }
              const isNewOption = newValue.id === 'new';
              field.onChange({ name: newValue.name, id: isNewOption ? null : newValue.id });
            }}
            filterOptions={(opts, state: FilterOptionsState<AutocompletePointOfSaleType>) => {
              const filtered = filterPointsOfSale(opts, state);
              if (state.inputValue.length) {
                filtered.push({
                  label: `${translate(LangEnum.GLOBAL_ADD)} "${state.inputValue}"`,
                  name: state.inputValue,
                  id: 'new',
                });
              }
              return filtered;
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <MenuItem {...optionProps} key={key}>
                  {option.label ? (
                    <Stack
                      direction="row"
                      sx={{ gap: 1, alignItems: 'center', color: 'primary.main' }}
                    >
                      <Icon icon={Plus} size="sm" />
                      <Typography color="primary">{option.label}</Typography>
                    </Stack>
                  ) : (
                    option.name
                  )}
                </MenuItem>
              );
            }}
          />
        );
      }}
    />
  );
};

export default PosAutocomplete;
